import { useState } from "react";
import { Globe, Lock, Check, type LucideIcon } from "lucide-react";

import { TarefaVisibilidade } from "@workflow/types";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SidebarHighlightCard } from "./SidebarHighlightCard";

interface TarefaVisibilidadeCardProps {
  visibilidade?: TarefaVisibilidade;
  /** Criador/admin, tarefa genérica e aberta. */
  canEdit: boolean;
  isLoading: boolean;
  onUpdate: (visibilidade: TarefaVisibilidade) => void;
}

/** Aparência (ícone, cor e textos) de cada visibilidade. */
const VISIBILIDADE_UI: Record<
  TarefaVisibilidade,
  { icon: LucideIcon; label: string; resumo: string; explicacao: string; color: string; bg: string }
> = {
  [TarefaVisibilidade.PUBLICA]: {
    icon: Globe,
    label: "Pública",
    resumo: "Todos no job",
    explicacao: "Qualquer pessoa com acesso ao job consegue ver esta tarefa.",
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/20",
  },
  [TarefaVisibilidade.PRIVADA]: {
    icon: Lock,
    label: "Privada",
    resumo: "Só os envolvidos",
    explicacao: "Aparece apenas para o responsável, o criador e os observadores.",
    color: "text-amber-600 dark:text-amber-500",
    bg: "bg-amber-500/10 border-amber-500/20",
  },
};

/**
 * Card de destaque para a visibilidade da tarefa, no mesmo espírito do
 * TarefaDataPrazoCard: mostra a visibilidade atual e o que ela implica e, quando
 * editável, abre um popover para trocar (salva na hora, sem botão de confirmar).
 */
export function TarefaVisibilidadeCard({
  visibilidade,
  canEdit,
  isLoading,
  onUpdate,
}: TarefaVisibilidadeCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!visibilidade) return null;

  const ui = VISIBILIDADE_UI[visibilidade];

  const handleSelect = (next: TarefaVisibilidade) => {
    if (next !== visibilidade) onUpdate(next);
    setIsOpen(false);
  };

  const card = (
    <SidebarHighlightCard
      icon={ui.icon}
      title="Visibilidade"
      tag={ui.label}
      value={ui.resumo}
      description={ui.explicacao}
      color={ui.color}
      bg={ui.bg}
      editable={canEdit}
      isLoading={isLoading}
    />
  );

  if (!canEdit) return card;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild disabled={isLoading}>
        <button type="button" className="w-full appearance-none text-left">
          {card}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-72 flex-col gap-2">
        <span className="px-1 text-sm font-medium text-foreground">Quem pode ver esta tarefa?</span>
        {(Object.keys(VISIBILIDADE_UI) as TarefaVisibilidade[]).map((opcao) => (
          <VisibilidadeOption
            key={opcao}
            ui={VISIBILIDADE_UI[opcao]}
            active={opcao === visibilidade}
            onClick={() => handleSelect(opcao)}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
}

function VisibilidadeOption({
  ui,
  active,
  onClick,
}: {
  ui: (typeof VISIBILIDADE_UI)[TarefaVisibilidade];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-2.5 rounded-lg border p-3 text-left text-sm transition-all",
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary/30"
          : "border-border/60 hover:bg-muted/40",
      )}
    >
      <ui.icon
        className={cn("mt-0.5 size-4 shrink-0", active ? "text-primary" : "text-muted-foreground")}
      />
      <div className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1.5 font-medium text-foreground">
          {ui.label}
          {active && <Check className="h-3.5 w-3.5 text-primary" />}
        </span>
        <span className="text-xs leading-snug text-muted-foreground">{ui.explicacao}</span>
      </div>
    </button>
  );
}
